import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type {
  DocumentVersionOption,
  FilingCheckRow,
  PeriodOption,
  TaxHandoffPreview,
  TaxHandoffRow,
} from '@/core/models/phase4.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { useClientType } from '@/core/useClientType';
import { phase4Service } from '@/core/services/phase4';
import { Button } from '@/kit/Button';
import { DataTable } from '@/kit/DataTable';
import { EmptyRow } from '@/features/EmptyRow';
import { Icon } from '@/kit/Icon';
import { Select, SelectOption } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { formatDateOnly, formatDateTime, formatNumber, useBusy, useClientGuard, useLatest } from './shared';
import './TaxreturnTaxHandoff.scss';

/**
 * AP-015 Tax handoff: hands one exact approved accounts version to the Corporation Tax workflow for the matching tax
 * period. The version and period are always chosen explicitly - nothing is attached automatically.
 *
 * A sole trader, partnership or LLP is not charged Corporation Tax: the same screen hands the final profit to Self Assessment
 * (SA103/SA105; each partner's SA104 and the partnership's SA800; an LLP member's SA104).
 */
export function TaxreturnTaxHandoff() {
  const navigate = useNavigate();
  const client = useClientGuard();
  const { isBusy, begin, end } = useBusy();

  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  const [documents, setDocuments] = useState<DocumentVersionOption[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  const [preview, setPreview] = useState<TaxHandoffPreview | null>(null);
  const [handoffs, setHandoffs] = useState<TaxHandoffRow[]>([]);
  const [handoffsLoaded, setHandoffsLoaded] = useState(false);

  // The client's type is known before a version is chosen, so the labels are right from the first render.
  const clientType = useClientType(client?.id);
  const selfAssessment = preview ? preview.taxTarget === 'SELF_ASSESSMENT' : !!clientType;
  const isIncorporated = clientType === 0 || clientType === 3;

  const current = useLatest({ periodId: selectedPeriodId, documentId: selectedDocumentId });

  const loadPreview = (periodId: string | null, documentId: string | null) => {
    if (!periodId || !documentId) {
      return;
    }
    phase4Service.previewHandoff(periodId, documentId).then(
      (result) => setPreview(result),
      (error) => notifyError(error),
    );
  };

  const loadHandoffs = (periodId: string | null) => {
    if (!periodId) {
      return;
    }
    phase4Service.getHandoffs(periodId).then(
      (rows) => {
        setHandoffs(rows);
        setHandoffsLoaded(true);
      },
      (error) => notifyError(error),
    );
  };

  const onPeriodChange = (options: PeriodOption[], periodId: string | null) => {
    const period = options.find((p) => p.accountingPeriodId === periodId);
    const list = period ? period.documents : [];
    setDocuments(list);

    // Only an approved or finalised version is a defensible basis for a tax computation.
    const eligible = list.filter((d) => d.status === 3 || d.status === 4);
    const documentId = eligible.length > 0 ? eligible[0].documentVersionId : null;
    setSelectedDocumentId(documentId);

    setPreview(null);
    loadHandoffs(periodId);
    loadPreview(periodId, documentId);
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    phase4Service.getPeriodOptions(client.id).then(
      (rows) => {
        setPeriods(rows);
        if (rows.length > 0) {
          setSelectedPeriodId(rows[0].accountingPeriodId);
          onPeriodChange(rows, rows[0].accountingPeriodId);
        }
      },
      (error) => notifyError(error),
    );
    // Runs once, when the screen opens (ngOnInit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  /** Hands over the exact version shown in the preview, for the exact matching period. */
  const handOff = () => {
    if (!selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!selectedDocumentId) {
      notifyError('Select an accounts version first.');
      return;
    }
    if (!preview || isBusy) {
      return;
    }
    if (!preview.canHandOff) {
      notifyError(
        selfAssessment
          ? 'Resolve the blocking issues before handing these accounts to Self Assessment.'
          : 'Resolve the blocking issues before handing these accounts to Corporation Tax.',
      );
      return;
    }
    if (!begin()) {
      return;
    }
    phase4Service
      .handOff({
        accountingPeriodId: selectedPeriodId,
        documentVersionId: selectedDocumentId,
        taxPeriodStart: preview.periodStart,
        taxPeriodEnd: preview.periodEnd,
      })
      .then(
        (row) => {
          const handedTo = selfAssessment ? 'handed to Self Assessment:' : 'handed to Corporation Tax:';
          notifySuccess(
            `Accounts v${row.documentVersionNo} ${handedTo} ${row.statusName}` +
              (row.ctReference ? ` (${row.ctReference}).` : '.'),
          );
          loadHandoffs(current.current.periodId);
          loadPreview(current.current.periodId, current.current.documentId);
        },
        (error) => notifyError(error),
      )
      .finally(end);
  };

  const resolve = (check: FilingCheckRow) => {
    if (check.route) {
      void navigate(check.route);
    }
  };

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-tax-handoff">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              clearable={false}
              placeholder="Accounting period"
              value={selectedPeriodId}
              onChange={(value) => {
                const periodId = (value as string | null) ?? null;
                setSelectedPeriodId(periodId);
                onPeriodChange(periods, periodId);
              }}
            >
              {periods.map((option) => (
                <SelectOption key={option.accountingPeriodId} value={option.accountingPeriodId}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              clearable={false}
              placeholder="Accounts version"
              value={selectedDocumentId}
              onChange={(value) => {
                const documentId = (value as string | null) ?? null;
                setSelectedDocumentId(documentId);
                setPreview(null);
                loadPreview(selectedPeriodId, documentId);
              }}
            >
              {documents.map((option) => (
                <SelectOption key={option.documentVersionId} value={option.documentVersionId}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          </div>

          <div className="content-header-right mb-1">
            <Button
              className="action-button primary-button"
              disabled={!preview || !preview.canHandOff || isBusy}
              onClick={handOff}
            >
              <Icon name="forward_to_inbox" outlined />
              <span>{selfAssessment ? 'Hand to Self Assessment' : 'Hand to Corporation Tax'}</span>
            </Button>
          </div>
        </div>

        {preview && (
          <div className="sub-wrap">
            <div className="sub-acc">
              <div className="sub-ttl">{isIncorporated || !selfAssessment ? 'Company :' : 'Business :'}</div>
              <div className="sub-dtl">
                {preview.companyName} {preview.companyNumber}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">UTR :</div>
              <div className="sub-dtl">{preview.utr || '-'}</div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Tax period :</div>
              <div className="sub-dtl">
                {formatDateOnly(preview.periodStart)} - {formatDateOnly(preview.periodEnd)}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Version :</div>
              <div className="sub-dtl">
                v{preview.documentVersionNo} ({preview.documentStatusName})
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Statutory profit :</div>
              <div className="sub-dtl">{formatNumber(preview.statutoryProfit)}</div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">ATB lines :</div>
              <div className="sub-dtl">{preview.atbLineCount}</div>
            </div>
            {selfAssessment ? (
              <div className="sub-acc">
                <div className="sub-ttl">Return :</div>
                <div className="sub-dtl">{preview.taxReturns || '-'}</div>
              </div>
            ) : (
              <div className="sub-acc">
                <div className="sub-ttl">iXBRL :</div>
                <div className={`sub-dtl ${preview.hasValidIxbrl ? 'text-green' : 'text-red'}`}>
                  {preview.ixbrlContentHash || 'Not generated'}
                </div>
              </div>
            )}
            <div className="sub-acc">
              <div className="sub-ttl">{selfAssessment ? 'SA integration :' : 'CT integration :'}</div>
              <div className={`sub-dtl ${preview.isConfigured ? 'text-green' : 'text-red'}`}>
                {preview.isConfigured ? 'Configured' : 'Not configured'}
              </div>
            </div>
          </div>
        )}

        {preview && preview.checks.length > 0 && (
          <div className="mat-table-wrapper mb-2">
            <DataTable
              stickyHeader
              rows={preview.checks}
              rowKey={(row, index) => `${row.srNo}-${index}`}
              columns={[
                { id: 'srno', header: 'Sr. No', cell: (c) => c.srNo },
                { id: 'check', header: 'Check', cell: (c) => c.check },
                {
                  id: 'severity',
                  header: 'Severity',
                  cellClassName: (c) => (c.isBlocking ? 'text-red' : undefined),
                  cell: (c) => c.severity,
                },
                { id: 'message', header: 'Message', cell: (c) => c.message },
                {
                  id: 'link',
                  header: 'Link',
                  cell: (c) =>
                    c.route && (
                      <a className="link" onClick={() => resolve(c)}>
                        Resolve
                      </a>
                    ),
                },
              ]}
            />
          </div>
        )}

        <div className="form-field">
          <p>Handoff history</p>
        </div>

        <div className="mat-table-wrapper">
          <DataTable
            stickyHeader
            rows={handoffs}
            rowKey={(row) => row.taxHandoffId}
            noDataMessage={
              handoffsLoaded ? (
                <EmptyRow icon="history" title="No handoffs yet" hint="A handoff appears here once a version has been sent." />
              ) : undefined
            }
            columns={[
              {
                id: 'created',
                header: 'Created',
                cell: (h) => (
                  <>
                    {formatDateTime(h.createdAtUtc)}
                    <div className="text-gray">{h.createdByName}</div>
                  </>
                ),
              },
              { id: 'version', header: 'Version', cell: (h) => `v${h.documentVersionNo}` },
              {
                id: 'period',
                header: 'Tax period',
                cell: (h) => `${formatDateOnly(h.taxPeriodStart)} - ${formatDateOnly(h.taxPeriodEnd)}`,
              },
              { id: 'profit', header: 'Statutory profit', cell: (h) => formatNumber(h.statutoryProfit) },
              {
                id: 'status',
                header: 'Status',
                cellClassName: (h) => (h.status === 3 || h.status === 4 ? 'text-red' : undefined),
                cell: (h) => h.statusName,
              },
              { id: 'reference', header: selfAssessment ? 'SA reference' : 'CT reference', cell: (h) => h.ctReference || '-' },
            ]}
          />
        </div>
      </div>
    </SideMenuPage>
  );
}
